import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Platform } from '@app/classes';
import { FeatureFlag } from '@app/enums';
import { CompanyModuleGuard, FeatureGuard, PermissionGuard, WithoutPermissionGuard } from '@app/guards';
import { RoutesWithOptions } from '@app/interfaces';
import { BulkImportEntityView } from '@reporting/views/bulk-import/bulk-import-entity/bulk-import-entity.view';
import { BulkImportResultsView } from '@reporting/views/bulk-import/bulk-import-results/bulk-import-results.view';
import { BulkImportTimeOff } from '@reporting/views/bulk-import/bulk-import-time-off/bulk-import-time-off.view';
import { AccrualPolicyGuard } from '@time-off-v3/guards/accrual-policy.guard';
import { EmployeeResolver } from '@time-off-v3/resolvers/employee.resolver';
import { RequestRulesUpdateView } from '@time-off-v3/views/policy/update/request-rules/request-rules-update.view';
import { RequestIndexView } from '@time-off-v3/views/request/index/request-index.view';
import { TimeOffCalendarView } from '../common/views';
import { TimeOffV3Resolver } from './time-off-v3.resolver';
import {
    AccrualUpdateView,
    ApprovalFlowUpdateView,
    AssignPolicyView,
    BasicInformationUpdateView,
    GeneralSettingsEditView,
    PolicyCreateView,
    PolicyDetailsEnrolledEmployeesView,
    PolicyShowView,
    PolicyStoreView,
    PolicyUpdateView,
    RequestCreateView,
    RequestShowView,
    RequestUpdateView,
    SettingsShowView,
    SettingsWorkScheduleUpdateView,
    TeamView,
    TypesAndPolicyIndexView,
    TypesStoreView,
    TypesUpdateView,
    WorkScheduleOverallSettingsUpdateView,
    WorkScheduleUpdateView,
    WorkSchedulesEnrolledEmployeesView,
} from './views';

const routes: RoutesWithOptions = [
    {
        path: '',
        redirectTo: 'requests',
        pathMatch: 'full',
    },
    {
        path: 'team',
        component: TeamView,
        canActivate: [PermissionGuard, WithoutPermissionGuard],
        data: {
            cannot: ['administrate'],
            withoutPermissionRedirectTo: ['/time-off', 'v3', 'requests'],
            permission: ['manageDirectReports'],
            canActivateRedirectTo: ['/profile', 'time-off', 'v3'],
        },
    },
    {
        /**
         * @deprecated
         * Redirects to route below
         */
        path: 'calendar',
        redirectTo: '/calendar/company/time-off',
    },
    {
        path: 'company/time-off',
        component: TimeOffCalendarView,
        canActivate: [CompanyModuleGuard, PermissionGuard],
        data: {
            companyModule: Platform.modules.timeOff,
            permission: Platform.timeOff.permission.calendar,
        },
    },
    {
        path: 'requests',
        children: [
            {
                path: '',
                canActivate: [PermissionGuard],
                data: {
                    permission: 'administrate',
                    canActivateRedirectTo: ['/profile', 'time-off', 'v3'],
                },
                component: RequestIndexView,
                pathMatch: 'full',
            },
            {
                path: 'create',
                component: RequestCreateView,
                resolve: {
                    models: EmployeeResolver,
                },
            },
            {
                path: ':request',
                resolve: {
                    models: TimeOffV3Resolver,
                },
                component: RequestShowView,
            },
            {
                path: ':request/edit',
                resolve: {
                    models: TimeOffV3Resolver,
                },
                component: RequestUpdateView,
            },
        ],
    },
    {
        path: 'policy',
        canActivate: [PermissionGuard, FeatureGuard],
        data: {
            permission: 'administrate',
            canActivateRedirectTo: ['/dashboard'],
            feature: {
                flags: [FeatureFlag.timeOffImprovements],
            },
        },
        children: [
            {
                path: 'create',
                component: PolicyCreateView,
            },
            {
                path: ':policy',
                resolve: {
                    models: TimeOffV3Resolver,
                },
                runGuardsAndResolvers: 'always',
                children: [
                    {
                        path: '',
                        redirectTo: 'details',
                        pathMatch: 'full',
                    },
                    {
                        path: 'details',
                        component: PolicyShowView,
                    },
                    {
                        path: 'enrolled-employees',
                        component: PolicyDetailsEnrolledEmployeesView,
                    },
                    {
                        path: 'basic-information',
                        component: BasicInformationUpdateView,
                    },
                    {
                        path: 'rules',
                        canActivate: [AccrualPolicyGuard],
                        component: RequestRulesUpdateView,
                    },
                    {
                        path: 'accrual',
                        component: AccrualUpdateView,
                    },
                ],
            },
        ],
    },
    // Import Reports
    {
        path: 'import',
        canActivate: [PermissionGuard, FeatureGuard],
        data: {
            permission: 'administrate',
            canActivateRedirectTo: ['/dashboard'],
            feature: {
                flags: [FeatureFlag.timeOffImprovements],
            },
        },
        children: [
            { path: ':entity', component: BulkImportEntityView },
            {
                path: 'time-off-importers/:entity',
                component: BulkImportTimeOff,
            },
            { path: ':entity/results/:file_id', component: BulkImportResultsView },
        ],
    },
    {
        path: 'types-and-policies',
        canActivate: [PermissionGuard],
        data: {
            permission: 'administrate',
            canActivateRedirectTo: ['/profile', 'time-off', 'v3'],
        },
        children: [
            {
                path: '',
                component: TypesAndPolicyIndexView,
            },
            {
                path: 'types/create',
                component: TypesStoreView,
            },
            {
                path: 'policy/create',
                component: PolicyStoreView,
            },
            {
                path: 'policy/assign/:policyId',
                component: AssignPolicyView,
            },
            {
                path: 'types/:type/edit',
                resolve: {
                    models: TimeOffV3Resolver,
                },
                component: TypesUpdateView,
            },
            {
                path: 'policies/:policy/edit',
                resolve: {
                    models: TimeOffV3Resolver,
                },
                component: PolicyUpdateView,
            },
        ],
    },
    {
        path: 'settings',
        canActivate: [PermissionGuard],
        data: {
            permission: 'administrate',
            canActivateRedirectTo: ['/profile', 'time-off', 'v3'],
        },
        children: [
            {
                path: '',
                component: SettingsShowView,
            },
            {
                path: 'edit',
                canActivate: [FeatureGuard],
                data: {
                    feature: {
                        flags: [FeatureFlag.timeOffImprovements],
                    },
                },
                component: GeneralSettingsEditView,
            },
        ],
    },
    {
        path: 'work-schedule',
        canActivate: [PermissionGuard],
        data: {
            permission: 'administrate',
            canActivateRedirectTo: ['/profile', 'time-off', 'v3'],
        },
        children: [
            {
                path: ':work-schedule',
                resolve: {
                    models: TimeOffV3Resolver,
                },
                runGuardsAndResolvers: 'always',
                children: [
                    {
                        path: 'edit',
                        component: WorkScheduleUpdateView,
                    },
                    {
                        path: 'details',
                        component: SettingsWorkScheduleUpdateView,
                        canActivate: [FeatureGuard],
                        data: {
                            feature: {
                                flags: [FeatureFlag.timeOffImprovements],
                            },
                        },
                    },
                    {
                        path: 'enrolled-employees',
                        component: WorkSchedulesEnrolledEmployeesView,
                        canActivate: [FeatureGuard],
                        data: {
                            feature: {
                                flags: [FeatureFlag.timeOffImprovements],
                            },
                        },
                    },
                    {
                        path: 'overall-settings',
                        component: WorkScheduleOverallSettingsUpdateView,
                        canActivate: [FeatureGuard],
                        data: {
                            feature: {
                                flags: [FeatureFlag.timeOffImprovements],
                            },
                        },
                    },
                ],
            },
        ],
    },
    {
        path: 'approval-flow',
        canActivate: [PermissionGuard],
        data: {
            permission: 'administrate',
            canActivateRedirectTo: ['/profile', 'time-off', 'v3'],
        },
        children: [
            {
                path: ':approval-flow/edit',
                resolve: {
                    models: TimeOffV3Resolver,
                },
                component: ApprovalFlowUpdateView,
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class TimeOffV3RouterModule {}
